import { useCallback } from 'react'
import { useLatest } from 'react-use'
import { isArray } from 'lodash-es'
import { ThreeEvent } from '@react-three/fiber'
import { useIfcImportSelectionActive } from '@editorHooks'
import { isPointerModifierPressed } from 'src/shortcuts/pointerModifiers'
import { EventTypes, ModelMeshClickListener, ModelMeshEventData } from '../state/store'

interface ListenerConfig {
  listener: ModelMeshClickListener
  guids: string | string[] | null
  eventType: EventTypes | EventTypes[]
  enableOnIfcMode?: boolean
  /**
   * Enable this callback while the multi-select modifier is held
   */
  enableOnMultiSelect?: boolean
}

export const listensers: Map<string, ListenerConfig> = new Map()

const useEmitModelClickListenerV2 = () => {
  const ifcDrawer = useIfcImportSelectionActive()
  const latestIfcDrawer = useLatest(ifcDrawer)

  const executeListener = useCallback<ModelMeshClickListener>(
    (guid: string, event: ThreeEvent<MouseEvent>, data: ModelMeshEventData) => {
      event.stopPropagation()

      const multiSelectPressed = isPointerModifierPressed('multiSelect')
      const listenersArray = Array.from(listensers.values())
      const relevantListeners = listenersArray.filter(listenerConfig => {
        if (multiSelectPressed && !listenerConfig.enableOnMultiSelect) return false
        if (listenerConfig.enableOnIfcMode && !latestIfcDrawer.current) return false

        if (isArray(listenerConfig.eventType) && !listenerConfig.eventType.includes(data.type))
          return false
        if (!isArray(listenerConfig.eventType) && listenerConfig.eventType !== data.type)
          return false

        if (Array.isArray(listenerConfig.guids)) {
          return listenerConfig.guids.includes(guid)
        } else if (typeof listenerConfig.guids === 'string') {
          return listenerConfig.guids === guid
        } else if (listenerConfig.guids === null) {
          // This is the default case. Just explicitly return true here for readability.
          return true
        } else {
          return true
        }
      })

      relevantListeners.forEach(listenerConfig => listenerConfig.listener(guid, event, data))
    },
    [latestIfcDrawer],
  )

  return executeListener
}

export default useEmitModelClickListenerV2
